import React, { useState, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import axios from "axios";
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Button from "components/CustomButtons/Button.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import { localApi, credentials } from "utils/imgApi";
import styles from "assets/jss/material-dashboard-pro-react/views/registerPageStyle";

const useStyles = makeStyles(styles);

export default function CsvFileUpload() {
  const classes = useStyles();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState(""); // "success" or "error"
  const [downloadUrl, setDownloadUrl] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];

    // Validate file type
    if (
      selectedFile &&
      selectedFile.type !== "text/csv" &&
      !selectedFile.name.endsWith(".csv")
    ) {
      setMessage("Please select a valid CSV file");
      setMessageType("error");
      setFile(null);
      setDownloadUrl(null);
      return;
    }

    setFile(selectedFile);
    setMessage("");
    setMessageType("");
    setDownloadUrl(null);
  };

  const handleUpload = async () => {
    if (!file) {
      setMessage("Please select a file");
      setMessageType("error");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);

    try {
      const response = await axios.post(
        `${localApi}/contact/uploadCSV`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Basic ${credentials}`,
            Accept: "application/json",
          },
        },
      );

      setMessage(`Success! ${response.data.successCount} records imported.`);
      setMessageType("success");

      // Download rejected records
      if (response.data.rejectedCount > 0 && response.data.rejectedCSV) {
        const blob = new Blob([response.data.rejectedCSV], {
          type: "text/csv",
        });

        const url = window.URL.createObjectURL(blob);
        setDownloadUrl(url);
      } else {
        // Refresh page after successful upload
        setTimeout(() => {
          window.location.reload();
        }, 500);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || "Upload failed";

      setMessage(`Upload failed: ${errorMessage}`);
      setMessageType("error");
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = () => {
    if (downloadUrl) {
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `rejected_records_${new Date().getTime()}.csv`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Wait for download to start, then clean up and refresh page
      setTimeout(() => {
        window.URL.revokeObjectURL(downloadUrl);
        setDownloadUrl(null);
        window.location.reload();
      }, 500);
    }
  };

  const handleClear = () => {
    setFile(null);
    setMessage("");
    setMessageType("");
    setDownloadUrl(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className={classes.container}>
      <GridContainer justify="center">
        <GridItem xs={12} sm={10} md={8}>
          <Card>
            <CardBody>
              <h4>CSV File Upload</h4>

              {/* File Input Outside */}
              <div style={{ marginBottom: "20px" }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  disabled={uploading}
                  style={{
                    display: "block",
                    width: "100%",
                    padding: "10px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: uploading ? "#f5f5f5" : "#fff",
                    cursor: uploading ? "not-allowed" : "pointer",
                  }}
                />
              </div>

              <div style={{ marginTop: "20px" }}>
                <Button
                  color="primary"
                  onClick={handleUpload}
                  disabled={uploading || !file}
                  style={{ marginRight: "10px" }}
                >
                  {uploading ? "Uploading..." : "Upload CSV"}
                </Button>

                {file && (
                  <Button
                    color="gray"
                    onClick={handleClear}
                    disabled={uploading}
                  >
                    Clear
                  </Button>
                )}
              </div>

              {message && (
                <p
                  style={{
                    color: messageType === "error" ? "#f44336" : "#4caf50",
                    marginTop: "15px",
                  }}
                >
                  {message}
                </p>
              )}

              {downloadUrl && (
                <div style={{ marginTop: "15px" }}>
                  <Button color="warning" onClick={handleDownload}>
                    Download Rejected Records
                  </Button>

                  {file && (
                    <span
                      style={{
                        marginLeft: "10px",
                        color: "#666",
                      }}
                    >
                      ({file.name})
                    </span>
                  )}
                </div>
              )}
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    </div>
  );
}
